// all comments are lowercase per project rules

import { createGenerator, type UserConfig } from "@unocss/core";
import presetWind from "@unocss/preset-wind";

// allowed css variable tokens for project theme
const VAR_TOKENS = new Set([
  "foreground",
  "muted-foreground",
  "accent",
  "accent-foreground",
  "primary",
  "primary-foreground",
  "secondary",
  "secondary-foreground",
  "card",
  "card-foreground",
  "border",
]);

const unoPromise = createGenerator({
  presets: [presetWind()],
  // custom rules to map project theme tokens to css variables with optional opacity via color-mix
  rules: [
    [
      /^text-([a-z-]+)(?:\/(\d{1,3}))?$/,
      (match) => {
        const name = match[1];
        const rawAlpha = match[2];
        if (!VAR_TOKENS.has(name)) return;
        const v = `var(--${name})`;
        const alpha = typeof rawAlpha === "string" ? Math.max(0, Math.min(100, Number(rawAlpha))) : undefined;
        const value = typeof alpha === "number" ? `color-mix(in oklab, ${v} ${alpha}%, transparent)` : v;
        return { color: value } as Record<string, string>;
      },
    ],
    [
      /^bg-([a-z-]+)(?:\/(\d{1,3}))?$/,
      (match) => {
        const name = match[1];
        const rawAlpha = match[2];
        if (!VAR_TOKENS.has(name)) return;
        const v = `var(--${name})`;
        const alpha = typeof rawAlpha === "string" ? Math.max(0, Math.min(100, Number(rawAlpha))) : undefined;
        const value = typeof alpha === "number" ? `color-mix(in oklab, ${v} ${alpha}%, transparent)` : v;
        return { "background-color": value } as Record<string, string>;
      },
    ],
    [
      /^border-([a-z-]+)(?:\/(\d{1,3}))?$/,
      (match) => {
        const name = match[1];
        const rawAlpha = match[2];
        if (!VAR_TOKENS.has(name)) return;
        const v = `var(--${name})`;
        const alpha = typeof rawAlpha === "string" ? Math.max(0, Math.min(100, Number(rawAlpha))) : undefined;
        const value = typeof alpha === "number" ? `color-mix(in oklab, ${v} ${alpha}%, transparent)` : v;
        return { "border-color": value } as Record<string, string>;
      },
    ],
  ],
} as UserConfig);

// cache of individual utility tokens that have already been generated
const seen = new Set<string>();

let styleEl: HTMLStyleElement | null = null;
let observer: MutationObserver | null = null;
// map to manage per-target style and seen tokens for shadow roots or documents
const targetMap: WeakMap<Document | ShadowRoot, { styleEl: HTMLStyleElement; seen: Set<string> }> = new WeakMap();

function ensureStyleEl() {
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.setAttribute("data-uno-runtime", "true");
    document.head.appendChild(styleEl);
  }
}

function normalizeToken(token: string) {
  return token.trim();
}

export function isSafeClassToken(token: string) {
  // allow typical tailwind utility charset including arbitrary values in brackets
  // adjust this whitelist to your needs if you broaden arbitrary values
  return /^[-a-z0-9_:/.[\]#%,()]+$/i.test(token);
}

function tokenize(input: string) {
  return input.split(/\s+/).map(normalizeToken).filter(Boolean);
}

async function generateAndInject(tokens: string[]) {
  if (!tokens.length) return;
  const input = tokens.join(" ");
  const uno = await unoPromise;
  const { css } = await uno.generate(input, { minify: true });
  if (!css) return;
  ensureStyleEl();
  styleEl!.append(document.createTextNode(css));
}

export async function applyUtilities(input: string | string[]) {
  // accepts raw string or array of tokens and generates missing utilities only
  const tokens = Array.isArray(input) ? input : tokenize(input);
  const fresh: string[] = [];
  for (const t of tokens) {
    const token = normalizeToken(t);
    if (!token) continue;
    if (!isSafeClassToken(token)) continue;
    if (seen.has(token)) continue;
    fresh.push(token);
  }
  if (!fresh.length) return;
  await generateAndInject(fresh);
  for (const t of fresh) seen.add(t);
}

function collectElementClasses(el: Element, out: Set<string>) {
  // read class safely for both html and svg elements
  const cls = el.getAttribute("class") || "";
  if (cls) tokenize(cls).forEach((t) => out.add(t));
}

function collectTreeClasses(root: ParentNode) {
  const out = new Set<string>();
  if ((root as Element).nodeType === 1)
    collectElementClasses(root as Element, out);
  const all: NodeListOf<Element> = (root as Element).querySelectorAll
    ? (root as Element).querySelectorAll("*")
    : ([] as unknown as NodeListOf<Element>);
  for (const el of all) collectElementClasses(el, out);
  return out;
}

// shadow-root helpers
function ensureTargetStyle(target: Document | ShadowRoot) {
  const existing = targetMap.get(target);
  if (existing) {
    // re-attach if it was removed by a full shadow-root clear
    if (!existing.styleEl.isConnected) {
      if (target instanceof Document) {
        const head = target.head;
        head.insertBefore(existing.styleEl, head.firstChild);
      } else {
        (target as ShadowRoot).insertBefore(
          existing.styleEl,
          (target as ShadowRoot).firstChild
        );
      }
    }
    return existing;
  }
  const el = document.createElement("style");
  el.setAttribute("data-uno-runtime-target", "true");
  if (target instanceof Document) {
    const head = target.head;
    head.insertBefore(el, head.firstChild);
  } else {
    (target as ShadowRoot).insertBefore(el, (target as ShadowRoot).firstChild);
  }
  const entry = { styleEl: el, seen: new Set<string>() };
  targetMap.set(target, entry);
  return entry;
}

async function generateCssForTokens(tokens: string[]): Promise<string> {
  if (!tokens.length) return "";
  const input = tokens.join(" ");
  const uno = await unoPromise;
  const { css } = await uno.generate(input, { minify: true });
  return css || "";
}

export async function applyUtilitiesToTargetRoot(
  root: ParentNode,
  target: Document | ShadowRoot
) {
  const bucket = ensureTargetStyle(target);
  const set = collectTreeClasses(root);
  const fresh: string[] = [];
  set.forEach((t) => {
    const token = normalizeToken(t);
    if (!token) return;
    if (!isSafeClassToken(token)) return;
    if (bucket.seen.has(token)) return;
    fresh.push(token);
  });
  if (!fresh.length) return;
  const css = await generateCssForTokens(fresh);
  if (!css) return;
  bucket.styleEl.append(document.createTextNode(css));
  fresh.forEach((t) => bucket.seen.add(t));
}

export function initRuntimeCss() {
  // idempotent init
  if (observer) return;
  ensureStyleEl();

  const initial = collectTreeClasses(document.body);
  void applyUtilities(Array.from(initial));

  // observe class mutations and added nodes
  const pending = new Set<string>();
  let scheduled = false;

  function scheduleFlush() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(async () => {
      scheduled = false;
      if (!pending.size) return;
      const batch = Array.from(pending);
      pending.clear();
      await applyUtilities(batch);
    });
  }

  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (
        m.type === "attributes" &&
        m.attributeName === "class" &&
        m.target instanceof Element
      ) {
        const cls = (m.target as Element).getAttribute("class") || "";
        tokenize(cls).forEach((t) => pending.add(t));
      }
      if (m.type === "childList") {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) {
            const set = collectTreeClasses(n as ParentNode);
            set.forEach((t) => pending.add(t));
          }
        });
      }
    }
    scheduleFlush();
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["class"],
  });
}

export function stopRuntimeCss() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}
